// eslint-disable-next-line max-classes-per-file
import './bootstrap'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import '@v4/utils/validations'
import { AD_NETWORKS_QUERY_KEYS, BUILDER_VERSION } from '@v4/constants/general'

import { checkIsBuilderMode, checkIsScorecardMode } from '@v4/utils/general'
import { createBuilderStore } from '@v4/store/builderStore'
import AppLayout from '@v4/components/common/AppLayout'
import { createAppModel } from '@v4/store/appStore'
import { BrowserRouter } from 'react-router-dom'
import { createGenerateClassName, StylesProvider } from '@mui/styles'
import scoreappApi from '@v4/api/scoreappApi'
import PageData from '@v4/utils/data/PageData'
import { addMetaData, createErrorBoundary } from '@v4/utils/bugsnag'
import { storeSASIDtoUrl } from '@v4/utils/cookie'

const ErrorBoundary = createErrorBoundary()

const rootElement = document.getElementById('root')

if (rootElement) {
	rootElement.dataset.page = JSON.stringify({})
}

const isBuilderMode = checkIsBuilderMode(window.pageData.props.data.mode)

class FrameState {
	constructor({ active = false, root = rootElement, version, initialScroll }) {
		const state = { active, version, initialScroll }

		this.root = root
		this.state = state
		this.history = null
		this.handleMount(state)
	}

	async initApp(version, initialScroll) {
		const {
			component,
			props: { data },
		} = window.pageData

		if (
			!PageData.isTypeResultPdf(data.page?.type) &&
			(checkIsScorecardMode(data.mode) || component === 'AutoStart')
		) {
			const queryParams = new URLSearchParams(window.location.search)
			const splitTestId = _.get(data, 'split_test.id')
			const params = {
				referrer: document.referrer,
				url: window.location.href,
			}
			if (splitTestId) {
				params.splitTestId = splitTestId
			}

			AD_NETWORKS_QUERY_KEYS.forEach(queryKey => {
				const queryValue = queryParams.get(queryKey)
				if (queryValue) {
					params[queryKey] = queryValue
				}
			})

			await scoreappApi
				.get('/init', {
					baseURL: `${window.location.origin}${window.initialData.base_url || ''}`,
					params,
				})
				.then(({ data: { tokens } }) => {
					window.initialData.tokens = tokens
					data.tokens = tokens
					if (window.self !== window.top) {
						const widgetKey = new URLSearchParams(window.location.search).get('sa_widget') || ''

						window.parent.postMessage({ SASID: tokens.sasid, key: widgetKey }, '*')
					}
					storeSASIDtoUrl(tokens.sasid)
				})
				.catch(error => {
					// eslint-disable-next-line no-console
					console.error(error)
				})
		}

		window.pageData.url = `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`

		const builderStore = window?.builderStore || createBuilderStore(data)

		if (isBuilderMode && data.result) {
			builderStore.getActions().builder.setResult(_.cloneDeep(data.result))
		}

		const appModel = createAppModel(data, component)

		const modules = new Map()

		createInertiaApp({
			id: this.root.id,
			resolve: () => {
				if (version !== BUILDER_VERSION) {
					return resolvePageComponent(
						'/resources/react/v4/components/common/VersionError/index.js',
						import.meta.glob('/resources/react/v4/components/common/VersionError/index.js'),
					)
				}

				if (modules.has(component)) {
					return modules.get(component)
				}

				return resolvePageComponent(
					`/resources/react/v4/components/pages/${component}/${component}.jsx`,
					import.meta.glob('/resources/react/v4/components/pages/*/*.jsx'),
				).then(async module => {
					const { SectionManager } = module.default

					if (SectionManager) {
						const sectionLimit = +(import.meta.env.VITE_RENDERED_SECTION_COUNT || 0)
						let count = 0

						const alwaysVisible =
							(window.self !== window.top && !isBuilderMode) ||
							PageData.isTypeResultPdf(data.page.type) ||
							!sectionLimit

						const sections = alwaysVisible
							? data.page.sections
							: _.filter(data.page.sections, section => {
									const [group] = section.key.split('/')
									if (!_.includes(['popup', 'header', 'footer'], group)) {
										if (count >= sectionLimit) {
											return false
										}
										count += 1
									}

									return true
							  })
						const promises = sections.map(section => {
							const [group, key] = section.key.split('/')

							const variant = _.get(data.template, [
								'section_groups',
								group,
								'sections',
								key,
								'theme',
								'variant',
							])

							return SectionManager.load(group, variant)
						})

						await Promise.all(promises)
					}

					// eslint-disable-next-line no-param-reassign
					module.default.layout = page => (
						<AppLayout
							key={component}
							builderStore={builderStore}
							appModel={appModel}
							initialScroll={initialScroll}
						>
							{page}
						</AppLayout>
					)

					modules.set(component, module)

					return module
				})
			},
			setup: ({ el, App: InertiaApp, props }) => {
				const generateClassName = () =>
					createGenerateClassName({
						productionPrefix: 'score-app-',
					})

				this.reactRoot = createRoot(el)

				this.reactRoot.render(
					<ErrorBoundary>
						<StylesProvider generateClassName={generateClassName()}>
							<BrowserRouter>
								<InertiaApp {...props} initialPage={window.pageData} />
							</BrowserRouter>
						</StylesProvider>
					</ErrorBoundary>,
				)
			},
		})
	}

	setState(state) {
		this.state = { ...this.state, ...state }
		this.handleMount(state)
	}

	addBugsnagMetaData = (event, data) => {
		addMetaData({
			[event]: data,
		})
	}

	handleMount(state) {
		const { active = false, version, initialScroll } = state
		if (!active) this.reactRoot?.unmount(this.root)
		else {
			this.initApp(version, initialScroll)
		}
	}

	setHistory(history) {
		this.history = history
	}
}

if (isBuilderMode) {
	window.FrameState = new FrameState({ active: false })
	window.history.pushState = () => {}

	window.history.replaceState = () => {}

	class FakeUrl extends window.URL {
		constructor(...args) {
			if (args[0] && args[0].startsWith('http')) {
				super(...args)
			} else {
				super('http://scoreapp.local')
			}
		}
	}

	window.URL = FakeUrl
	window.addEventListener('unload', () => {
		window.FrameState.setState({ active: false })
	})
} else {
	const run = () => {
		window.FrameState = new FrameState({ active: true, version: BUILDER_VERSION })
	}

	run()
}
